import React, { useEffect, useState } from 'react';
import { Container, Grid, TextField, FormControl, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from '../../../../redux/store';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import PermissionRequired from '../../../errorPages/permissionRequired';
import { fCurrency } from '../../../../utils/formatNumber';
import useSettings from '../../../../hooks/useSettings';
import { accessVerify } from '../../../../utils/common';
import { getHomeDashboardData } from '../../../../redux/slices/homeRedux/homeRedux';
import moment from 'moment';
import { safeJSONParse, utcMoment } from '../../../../utils/common';
import { DatePicker } from '@mui/lab';

export default function DailySalesPaymentTypeReport() {
  const theme = useTheme();
  const { themeStretch } = useSettings();
  const dispatch = useDispatch();
  const { selectedLocation } = useSelector((state) => state.location);
  const { data: saleListData } = useSelector((state) => state.home);

  const [dailySales, setDailySales] = useState([]);
  const [reportMode, setReportMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [selectedDay, setSelectedDay] = useState(moment());

  const ALLOWED_PAYMENT_TYPES = ['Cash', 'Other Cards', 'Amex Cards', 'Pickme Food', 'Uber Eats', 'Voucher', 'Discount'];

  // Group sales by day and payment type
  const calculateDailySales = (data) => {
    const dailyAmounts = {};
    data?.forEach((sale) => {
      const day = moment(sale.pos_dateTime).utc().format('YYYY-MM-DD');
      if (!dailyAmounts[day]) dailyAmounts[day] = { total_gross_sales: 0 };

      sale?.pos_payments?.forEach((payment) => {
        const type = payment?.paymentTypesName;
        const amount = payment?.amount || 0;

        if (!ALLOWED_PAYMENT_TYPES.includes(type)) return;

        dailyAmounts[day][type] = (dailyAmounts[day][type] || 0) + amount;
      });

      dailyAmounts[day]['total_gross_sales'] = (dailyAmounts[day]['total_gross_sales'] || 0) + sale.pos_totalAmount;
    });

    // ✅ SORT BY DATE ASCENDING
    return Object.entries(dailyAmounts)
      .map(([date, payments]) => ({
        date,
        ...payments,
      }))
      .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
  };

  const getDailyReturningCustomerAmounts = (globalData, selectedLocationId, type) => {
    const uniquePhones = new Set();
    const dailyAmounts = {};

    const filteredData =
      globalData
        .sort((a, b) => moment(a.pos_dateTime).valueOf() - moment(b.pos_dateTime).valueOf())
        ?.filter((value) => utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day')))
        ?.filter((value) => {
          const phone = value?.customerPhone;

          // if (phone && !uniquePhones.has(phone) && !['94770884019'].includes(phone)) {
          if (phone && !['94770884019'].includes(phone)) {
            uniquePhones.add(phone);

            return type.includes(value?.pos_saleTypeId) && (Number(value?.pos_locationId) == Number(selectedLocationId) || selectedLocationId == 1);
          }

          return false;
        }) || [];

    filteredData.forEach((value) => {
      const day = moment(value?.pos_dateTime).utc().format('YYYY-MM-DD');

      if (!dailyAmounts[day]) {
        dailyAmounts[day] = 0;
      }

      const paymentData = safeJSONParse(value?.pos_payments);

      const depositedSale = paymentData?.reduce((acc, payment) => {
        let depositAmount = 0;

        switch (payment.paymentTypesName) {
          case 'Cash':
            depositAmount = payment.amount;
            break;
          case 'Other Cards':
          case 'Amex Cards':
            depositAmount = payment.amount * 0.97;
            break;
          case 'Pickme Food':
          case 'Uber Eats':
            depositAmount = payment.amount * 0.66;
            break;
          default:
            depositAmount = 0;
        }

        return acc + depositAmount;
      }, 0);

      dailyAmounts[day] += depositedSale;
    });

    return Object.entries(dailyAmounts)
      .map(([date, amount]) => ({
        date,
        returningCustomerDepositedSale: amount,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };
  const getDailyNewCustomerAmounts = (globalData, selectedLocationId, type) => {
    const uniquePhones = new Set();
    const dailyAmounts = {};

    const filteredData =
      globalData
        .sort((a, b) => moment(a.pos_dateTime).valueOf() - moment(b.pos_dateTime).valueOf())
        ?.filter((value) => !utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day')))
        ?.filter((value) => {
          const phone = value?.customerPhone;

          // if (phone && !uniquePhones.has(phone) && !['94770884019'].includes(phone)) {
          if (phone && !['94770884019'].includes(phone)) {
            uniquePhones.add(phone);

            return type.includes(value?.pos_saleTypeId) && (Number(value?.pos_locationId) == Number(selectedLocationId) || selectedLocationId == 1);
          }

          return false;
        }) || [];

    filteredData.forEach((value) => {
      const day = moment(value?.pos_dateTime).utc().format('YYYY-MM-DD');

      if (!dailyAmounts[day]) {
        dailyAmounts[day] = 0;
      }

      const paymentData = safeJSONParse(value?.pos_payments);

      const depositedSale = paymentData?.reduce((acc, payment) => {
        let depositAmount = 0;

        switch (payment.paymentTypesName) {
          case 'Cash':
            depositAmount = payment.amount;
            break;
          case 'Other Cards':
          case 'Amex Cards':
            depositAmount = payment.amount * 0.97;
            break;
          case 'Pickme Food':
          case 'Uber Eats':
            depositAmount = payment.amount * 0.66;
            break;
          default:
            depositAmount = 0;
        }

        return acc + depositAmount;
      }, 0);

      dailyAmounts[day] += depositedSale;
    });

    return Object.entries(dailyAmounts)
      .map(([date, amount]) => ({
        date,
        newCustomerDepositedSale: amount,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  // Fetch sales data whenever date selection or location changes
  useEffect(() => {
    if (!selectedLocation) return;

    const selectedDate = reportMode === 'day' ? selectedDay : selectedMonth;

    if (!selectedDate) return;

    const dateUnit = reportMode === 'day' ? 'day' : 'month';

    const startDate = moment(selectedDate).startOf(dateUnit).format('YYYY-MM-DD HH:mm:ss');

    const endDate = moment(selectedDate).endOf(dateUnit).format('YYYY-MM-DD HH:mm:ss');

    dispatch(
      getHomeDashboardData({
        fromDate: startDate,
        toDate: endDate,
        locationId: selectedLocation?.id,
      })
    );
  }, [reportMode, selectedMonth, selectedDay, selectedLocation?.id, dispatch]);

  // Parse sales and calculate daily totals
  useEffect(() => {
    if (!saleListData) {
      setDailySales([]);
      return;
    }

    if (saleListData.length === 0) {
      setDailySales([]);
      return;
    }

    const parsedData = saleListData
      ?.filter((sale) => ['Preparing', 'Served'].includes(sale?.pos_status))
      ?.map((sale) => ({
        ...sale,
        pos_items: safeJSONParse(sale?.pos_items),
        pos_payments: safeJSONParse(sale?.pos_payments),
      }));

    const dailyData = calculateDailySales(parsedData);

    const returningDaily = getDailyReturningCustomerAmounts(
      parsedData,
      selectedLocation?.id,
      [1, 2] // [1,2]
    );

    const newCustomersDaily = getDailyNewCustomerAmounts(
      parsedData,
      selectedLocation?.id,
      [1, 2] // [1,2]
    );

    const mergedData = dailyData.map((day) => {
      const returning = returningDaily.find((r) => r.date === day.date);
      const newCustomers = newCustomersDaily.find((r) => r.date === day.date);

      return {
        ...day,
        returningCustomerDepositedSale: returning?.returningCustomerDepositedSale || 0,
        newCustomerDepositedSale: newCustomers?.newCustomerDepositedSale || 0,
      };
    });

    setDailySales(mergedData);
  }, [saleListData]);

  // Table columns
  const columns = [
    { accessorKey: 'date', header: 'Date' },
    {
      accessorKey: 'total_gross_sales',
      header: 'Total Gross Sales',
      Cell: ({ cell, row }) => {
        return fCurrency(row.original.total_gross_sales || 0);
      },
    },
    ...ALLOWED_PAYMENT_TYPES.map((type) => ({
      accessorKey: type,
      header: type,
      Cell: ({ cell, row }) => {
        return fCurrency(row.original[type] || 0);
      },
    })),
    {
      accessorKey: 'returningCustomerDepositedSale',
      header: 'Returning Customer Deposited Sales',
      Cell: ({ row }) => fCurrency(row.original.returningCustomerDepositedSale || 0),
    },
    {
      accessorKey: 'newCustomerDepositedSale',
      header: 'New Customer Deposited Sales',
      Cell: ({ row }) => fCurrency(row.original.newCustomerDepositedSale || 0),
    },
  ];

  return (
    <Page title='Daily Sales Report'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs heading='Daily Sales Report' links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Reports', href: '' }, { name: 'Daily Sales Report' }]} />

        {/* Date Selector */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <ToggleButtonGroup
              color='primary'
              exclusive
              fullWidth
              value={reportMode}
              onChange={(event, newMode) => {
                if (!newMode) return;
                setReportMode(newMode);
              }}
            >
              <ToggleButton value='month'>Month</ToggleButton>
              <ToggleButton value='day'>Day</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              {reportMode === 'month' ? (
                <DatePicker
                  label='Select Month'
                  value={selectedMonth}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    setSelectedMonth(moment(newValue));
                  }}
                  views={['year', 'month']}
                  openTo='month'
                  renderInput={(params) => <TextField {...params} />}
                />
              ) : (
                <DatePicker
                  label='Select Day'
                  value={selectedDay}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    setSelectedDay(moment(newValue));
                  }}
                  renderInput={(params) => <TextField {...params} />}
                />
              )}
            </FormControl>
          </Grid>
        </Grid>

        {selectedLocation && accessVerify('DAILY_SALES_PAYMENT_VIEW') ? (
          <DataGridTable
            name='Daily Sales by Payment Type'
            data={dailySales}
            column={columns}
            isLoading={false}
            enableRowActions={false}
            enablePinning={false}
            enableRowSelection={false}
            isRowClickable={false}
            rowsPerPage={40}
          />
        ) : (
          <PermissionRequired />
        )}
      </Container>
    </Page>
  );
}
